import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';

import { base, isActiveRow, readPage, unwrap, withParty } from '@/app/cancellations/engine/api-shapes';
import {
	type ICancellationAlertSettings,
	type ICancellationEvidence,
	type ICancellationImpactSummary,
	type ICancellationPage,
	type ICancellationRow,
} from '@/app/cancellations/types';
import { httpStatusOf } from '@/app/live-election/engine/capability';
import { APPUSERROLES } from '@/constants/global';
import { coreApi } from '@/lib/api-client';

import { cancellationKeys } from './keys';

/** 404/405/403/401/429 will not fix themselves on an immediate retry. */
export const retryCancellation = (count: number, error: unknown) => {
	const s = httpStatusOf(error);
	return s !== 404 && s !== 405 && s !== 403 && s !== 401 && s !== 429 && count < 2;
};

export const fetchCancellationPage = async (
	electionId: number,
	params: URLSearchParams,
	signal?: AbortSignal
): Promise<ICancellationPage> => {
	const { data } = await coreApi.get(`${base(electionId)}?${params.toString()}`, { signal });
	return readPage(data, Number(params.get('pageNumber')) || 1, Number(params.get('pageSize')) || 20);
};

/** One page of the ledger. `params` is the exact query string sent (see `listParams`). */
export function useCancellationList(args: { electionId: number; partyKey: number; params: URLSearchParams; enabled: boolean }) {
	const { electionId, partyKey, params, enabled } = args;
	const qs = params.toString();
	return useQuery({
		queryKey: cancellationKeys.list(electionId, partyKey, qs),
		queryFn: ({ signal }) => fetchCancellationPage(electionId, new URLSearchParams(qs), signal),
		enabled: enabled && electionId > 0,
		staleTime: 15_000,
		retry: retryCancellation,
		placeholderData: keepPreviousData,
	});
}

/** Enough for any real INEC list; beyond it the set is marked truncated rather than fetched forever. */
export const ACTIVE_PAGE_CAP = 30;
const ACTIVE_PAGE_SIZE = 100;

export interface IActiveCancellations {
	rows: ICancellationRow[];
	truncated: boolean;
}

export const fetchAllActive = async (electionId: number, partyId: number | null, signal?: AbortSignal) => {
	const rows: ICancellationRow[] = [];
	let truncated = false;
	for (let page = 1; page <= ACTIVE_PAGE_CAP; page++) {
		const params = new URLSearchParams({ pageNumber: String(page), pageSize: String(ACTIVE_PAGE_SIZE) });
		if (partyId !== null && partyId > 0) params.set('partyId', String(partyId));
		const result = await fetchCancellationPage(electionId, params, signal);
		// The default state filter is Active, but a v1 server has no state at all — keep the guard.
		rows.push(...result.rows.filter(isActiveRow));
		const total = result.totalCount;
		const done = result.rows.length < ACTIVE_PAGE_SIZE || (total !== undefined && page * ACTIVE_PAGE_SIZE >= total);
		if (done) return { rows, truncated: false };
		if (page === ACTIVE_PAGE_CAP) truncated = true;
	}
	return { rows, truncated };
};

/**
 * Every ACTIVE cancellation for this party — the set the result surfaces subtract from their
 * denominators, the alert feed diffs against, and the bulk dry-run checks for duplicates.
 */
export function useActiveCancellations(args: {
	electionId: number;
	partyId: number | null;
	partyKey: number;
	enabled: boolean;
	refetchInterval?: number | false;
}) {
	const { electionId, partyId, partyKey, enabled, refetchInterval = false } = args;
	return useQuery({
		queryKey: cancellationKeys.active(electionId, partyKey),
		queryFn: ({ signal }): Promise<IActiveCancellations> => fetchAllActive(electionId, partyId, signal),
		enabled: enabled && electionId > 0 && partyKey > 0,
		staleTime: 20_000,
		refetchInterval,
		refetchIntervalInBackground: true,
		retry: retryCancellation,
	});
}

/** `ReadonlySet` of active codes, or null when unknown (so callers change nothing). */
export const useActiveCodeSet = (rows: ICancellationRow[] | undefined): ReadonlySet<string> | null =>
	useMemo(() => (rows ? new Set(rows.map((r) => r.pollingUnitCode)) : null), [rows]);

export function useCancellationImpactSummary(args: {
	electionId: number;
	partyKey: number;
	params: URLSearchParams;
	enabled: boolean;
}) {
	const { electionId, partyKey, params, enabled } = args;
	// Same filters as the list, minus paging.
	const filtered = new URLSearchParams(params);
	filtered.delete('pageNumber');
	filtered.delete('pageSize');
	const qs = filtered.toString();
	return useQuery({
		queryKey: cancellationKeys.impactSummary(electionId, partyKey, qs),
		queryFn: async ({ signal }): Promise<ICancellationImpactSummary> => {
			const { data } = await coreApi.get(`${base(electionId)}/impact-summary${qs ? `?${qs}` : ''}`, { signal });
			return unwrap<ICancellationImpactSummary>(data);
		},
		enabled: enabled && electionId > 0,
		staleTime: 15_000,
		retry: retryCancellation,
	});
}

export function useCancellationEvidence(args: {
	electionId: number;
	partyId: number | null;
	partyKey: number;
	id: number;
	enabled: boolean;
}) {
	const { electionId, partyId, partyKey, id, enabled } = args;
	return useQuery({
		queryKey: cancellationKeys.evidence(electionId, partyKey, id),
		queryFn: async ({ signal }): Promise<ICancellationEvidence[]> => {
			const { data } = await coreApi.get(withParty(`${base(electionId)}/${id}/evidence`, partyId), { signal });
			const list = unwrap<ICancellationEvidence[]>(data);
			return Array.isArray(list) ? list : [];
		},
		enabled: enabled && electionId > 0 && id > 0,
		// Signed URLs live ~1h; refetch well before they lapse under an open panel.
		staleTime: 10 * 60_000,
		refetchInterval: 40 * 60_000,
		retry: retryCancellation,
	});
}

export function useCancellationAlertSettings(args: {
	electionId: number;
	partyId: number | null;
	partyKey: number;
	enabled: boolean;
}) {
	const { electionId, partyId, partyKey, enabled } = args;
	return useQuery({
		queryKey: cancellationKeys.alertSettings(electionId, partyKey),
		queryFn: async ({ signal }): Promise<ICancellationAlertSettings> => {
			const { data } = await coreApi.get(withParty(`${base(electionId)}/alert-settings`, partyId), { signal });
			const raw = unwrap<Partial<ICancellationAlertSettings>>(data) ?? {};
			return {
				candidateSms: raw.candidateSms === true,
				channel: raw.channel === 'WhatsApp' ? 'WhatsApp' : 'Sms',
				recipientUserIds: Array.isArray(raw.recipientUserIds) ? raw.recipientUserIds : [],
			};
		},
		enabled: enabled && electionId > 0,
		staleTime: 60_000,
		retry: retryCancellation,
	});
}

export interface IRecipientOption {
	id: number;
	name: string;
	role: string;
	phone: string | null;
}

interface IUserLike {
	id?: number;
	firstName?: string;
	lastName?: string;
	email?: string;
	phoneNumber?: string;
}

/**
 * Who can be texted: the party's Party Admins and Candidates. `Role` is a case-sensitive,
 * server-side filter and `PartyId` is a HEADER (as a query key it widens to every party —
 * `use-user-queries.ts`), so both are sent exactly as the candidate roster does.
 */
export function useAlertRecipients(partyKey: number, enabled: boolean) {
	return useQuery({
		queryKey: cancellationKeys.recipients(partyKey),
		queryFn: async ({ signal }): Promise<IRecipientOption[]> => {
			const roles = [APPUSERROLES.PARTYADMIN, APPUSERROLES.CANDIDATE];
			const lists = await Promise.all(
				roles.map(async (role) => {
					const { data } = await coreApi.get(`/api/v1/User?Role=${encodeURIComponent(role)}&PageSize=100`, {
						signal,
						headers: { PartyId: String(partyKey) },
					});
					const users = (Array.isArray(data?.data) ? data.data : []) as IUserLike[];
					return users
						.filter((u) => typeof u.id === 'number')
						.map((u) => ({
							id: u.id!,
							name: [u.firstName, u.lastName].filter((p) => p && p !== 'null').join(' ') || u.email || `User ${u.id}`,
							role,
							phone: u.phoneNumber && u.phoneNumber !== 'null' ? u.phoneNumber : null,
						}));
				})
			);
			const byId = new Map<number, IRecipientOption>();
			for (const u of lists.flat()) if (!byId.has(u.id)) byId.set(u.id, u);
			return [...byId.values()].sort((a, b) => a.name.localeCompare(b.name));
		},
		enabled: enabled && partyKey > 0,
		staleTime: 5 * 60_000,
		retry: retryCancellation,
	});
}
